import { createContext, useContext, useState, useEffect } from 'react';

const FeatureContext = createContext({});

export const useFeatures = () => useContext(FeatureContext);

// Feature to route mapping - used by FeatureGate component
const FEATURE_ROUTE_MAP = {
  // Wallet features - all wallet routes check multiple feature flags
  wallet: ['/wallet'],
  buy_coins: ['/wallet'],
  withdraw_coins: ['/wallet'],
  wallet_deposit: ['/wallet'],
  wallet_withdraw: ['/wallet'],
  // KYC
  kyc: ['/kyc', '/kyc/basic', '/kyc/withdrawal'],
  // Challenges
  challenges: ['/challenges', '/create-challenge'],
  create_challenge: ['/create-challenge'],
  my_challenges: ['/challenges'],
  // Tournaments
  tournaments: ['/tournaments'],
  create_tournament: ['/tournaments'],
  // Matchmaking
  matchmaking: ['/matchmaking'],
  // Streaming
  livestream: ['/livestream'],
  streaming: ['/streams', '/livestream'],
  streams: ['/streams'],
  stream_and_live: ['/streams', '/livestream'],
  // Social
  leaderboard: ['/leaderboard'],
  referrals: ['/profile'],
  referral_program: ['/profile'],
  // User features
  profile: ['/profile'],
  notifications: ['/notifications'],
  // Players/Backing
  back_players: ['/players', '/me/backings'],
  backing: ['/players', '/me/backings'],
  search_players: ['/players'],
};

export const FeatureProvider = ({ children }) => {
  const [features, setFeatures] = useState({
    // Auth features
    signup: true,
    login: true,
    google_auth: true,
    registration: true,
    // User features
    profile: true,
    change_password: true,
    notifications: true,
    search_players: true,
    // Wallet features
    wallet: true,
    buy_coins: true,
    wallet_deposit: true,
    withdraw_coins: true,
    wallet_withdraw: true,
    kyc: true,
    // Challenge features
    challenges: true,
    create_challenge: true,
    my_challenges: true,
    back_players: true,
    // Tournament features
    tournaments: true,
    create_tournament: true,
    // Matchmaking
    matchmaking: true,
    // Streaming features
    stream_and_live: true,
    streaming: true,
    livestream: true,
    streams: true,
    // Social features
    leaderboard: true,
    referrals: true,
    referral_program: true,
    // Admin features
    admin_dashboard: true,
    dispute_tribunal: true,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeatures();
    // Refresh features every 60 seconds
    const interval = setInterval(fetchFeatures, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchFeatures = async () => {
    try {

    } catch (error) {
      console.error('Failed to fetch feature status');
    } finally {
      setLoading(false);
    }
  };

  const isEnabled = (featureName) => {
    return features[featureName] !== false;
  };

  // Check if a route is accessible based on feature flags
  const isRouteEnabled = (pathname) => {
    for (const [feature, routes] of Object.entries(FEATURE_ROUTE_MAP)) {
      for (const route of routes) {
        if (pathname.startsWith(route)) {
          if (features[feature] === false) {
            return false;
          }
        }
      }
    }
    return true;
  };

  // Get the feature name blocking a route (for error messages)
  const getBlockingFeature = (pathname) => {
    for (const [feature, routes] of Object.entries(FEATURE_ROUTE_MAP)) {
      for (const route of routes) {
        if (pathname.startsWith(route)) {
          if (features[feature] === false) {
            return feature;
          }
        }
      }
    }
    return null;
  };

  return (
    <FeatureContext.Provider value={{ 
      features, 
      isEnabled, 
      isRouteEnabled,
      getBlockingFeature,
      loading, 
      refetch: fetchFeatures 
    }}>
      {children}
    </FeatureContext.Provider>
  );
};

export default FeatureContext;
